import { useContext, useEffect, useState } from 'react';
import DOMPurify from 'dompurify'
import { SelectInput, Dialog, Icon} from "uxd-design-system-components";
import './LanguageSelect.scss';
import getText from '../../assets/locale/locale';
import { LOCALE_EN_CA, LOCALE_FR_CA } from "../../constants/constants";
import LocaleContext from '../../contexts/LocaleContext';
import PropTypes from 'prop-types'
import LanguageSelectProps from '../../types/components/LanguageSelectProps';



interface SelectOption {
  name: string;
  value: string;
  selected: boolean;
  disabled: boolean;
}
const LanguageSelect = (props: LanguageSelectProps) => {
  const { addBeforeUnloadEventListener, removeBeforeUnloadEventListener} = props;

  const locId = useContext(LocaleContext);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [url, setUrl] = useState("");
  const initialStateStatusOptions = [
    {
      name: "select language",
      selected: true,
      value: "select",
      disabled: false,
    },];
  const [options, setOptions] = useState<SelectOption[]>(initialStateStatusOptions);

  const handleCancel = () => {
    setOptions(getOptions());
    setShowConfirmDialog(false);
  }

  const INTEG_GATEWAY_URL_REGEX = /\.apps2\.devjones\.com$/;
  const UAT_GATEWAY_URL_REGEX = /\.apps2\.uat\.edwardjones\.com$/;
  const PROD_GATEWAY_URL_REGEX = /\.apps2\.edwardjones\.com$/;
  const UAT_LANGUAGE_URL_REGEX = /\.uat\.edwardjones\.ca$/;
  const PROD_LANGUAGE_URL_REGEX = /\.edwardjones\.ca$/;
  const UAT_EXTERNAL_TEST_URL_REGEX = /-uat\.testjones\.net$/;
  const PREPROD_EXTERNAL_TEST_URL_REGEX = /\.testjones\.net$/;

  const getSelectLanguageUrls = () => {
    const { hostname } = window.location;
    if (INTEG_GATEWAY_URL_REGEX.test(hostname)) {
      return `https://prp-mypriorities-gtwy.apps2.devjones.com?locale=${LOCALE_EN_CA},https://prp-mypriorities-gtwy.apps2.devjones.com?locale=${LOCALE_FR_CA}`;
    } else if (UAT_GATEWAY_URL_REGEX.test(hostname)) {
      return `https://prp-mypriorities-gtwy.apps2.uat.edwardjones.com?locale=${LOCALE_EN_CA},https://prp-mypriorities-gtwy.apps2.uat.edwardjones.com?locale=${LOCALE_FR_CA}`;
    } else if (PROD_GATEWAY_URL_REGEX.test(hostname)) {
      return `https://prp-mypriorities-gtwy.apps2.edwardjones.com?locale=${LOCALE_EN_CA},https://prp-mypriorities-gtwy.apps2.edwardjones.com?locale=${LOCALE_FR_CA}`;
    } else if (UAT_LANGUAGE_URL_REGEX.test(hostname)) {
      return 'https://mypriorities.uat.edwardjones.ca,https://mespriorites.uat.edwardjones.ca';
    } else if (PROD_LANGUAGE_URL_REGEX.test(hostname)) {
      return 'https://mypriorities.edwardjones.ca,https://mespriorites.edwardjones.ca';
    } else if (UAT_EXTERNAL_TEST_URL_REGEX.test(hostname)) {
      return `https://mypriorities-uat.testjones.net?locale=${LOCALE_EN_CA},https://mypriorities-uat.testjones.net?locale=${LOCALE_FR_CA}`;
    } else if (PREPROD_EXTERNAL_TEST_URL_REGEX.test(hostname)) {
      return `https://mypriorities.testjones.net?locale=${LOCALE_EN_CA},https://mypriorities.testjones.net?locale=${LOCALE_FR_CA}`;
    } else {
      return `http://localhost:1234/ca-en/index.html?locale=${LOCALE_EN_CA},http://localhost:1234/ca-fr/index.html?locale=${LOCALE_FR_CA}`;
    }
  };
  const displayDialog = (value: string) =>{
    setShowConfirmDialog(true);
    setUrl(value);
  }

  const handleLeave = () => {
    setShowConfirmDialog(false);
    const queryParams = new URLSearchParams(
      DOMPurify.sanitize(window.location.search)
    );
    let redirectUrl = url;
    queryParams.delete('locale');
    if (Array.from(queryParams).length > 0) {
      if (url.includes('?')) {
        redirectUrl = url.concat('&', queryParams.toString());
      } else {
        redirectUrl = url.concat('?', queryParams.toString());
      }
    }
    window.location.replace(redirectUrl);
  }

  const getOptions =() =>{
    const options = [];
    const languageSelectTexts = getText(locId, 'languageSelectTexts', 'LanguageSelect').split(',');
    const languageSelectUrls = getSelectLanguageUrls().split(',');
    const languageSelectTextDefault = getText(locId, 'languageSelectTextDefault', 'LanguageSelect');

    for (let index = 0; index < languageSelectTexts.length; index++) {
      const languageText = languageSelectTexts[index];
      const languageUrl = languageSelectUrls[index];
      const option = {
        name: languageText,
        selected: languageText === languageSelectTextDefault,
        value: languageUrl,
        disabled: false,
      };
      options.push(option);
    }
    return options;
  };

  useEffect(() => {
    if (locId === LOCALE_EN_CA || locId === LOCALE_FR_CA) {
      setOptions(getOptions());
    }
  }, []);

  useEffect(() => {
    if (locId === LOCALE_EN_CA || locId === LOCALE_FR_CA) {
      if (showConfirmDialog) {
        removeBeforeUnloadEventListener();
      } else {
        addBeforeUnloadEventListener();
      }
    }
  }, [showConfirmDialog]);

  return locId === LOCALE_EN_CA || locId === LOCALE_FR_CA ? (
    <div className="language-select">
      <div className="language-select--select-input">
        <SelectInput
          id="language-select-input"
          label={getText(locId, 'languageSelectLabel', 'LanguageSelect')}
          name="language-select-input"
          onSelect={(value: string) => {
            displayDialog(value);
          }}
          options={options}
        />
        {showConfirmDialog && (<Dialog className="dialog" ariaDescribedBy='cancel-dialogue' open={showConfirmDialog}
                                       buttons={[{
                                         onClick: () => {
                                           handleCancel();
                                         },
                                         text: getText(locId, 'languageDialogCancel', 'LanguageSelect')
                                       }, {
                                         attributes: {
                                           value: 'continue'
                                         },
                                         onClick: () => handleLeave(),
                                         styleVariant: 'primary',
                                         text: getText(locId, 'languageDialogLeave', 'LanguageSelect')
                                       }]}
                                       onRequestClose={() => {
                                         handleCancel();
                                       }}
                                       title="">
          <div className="dialog-content">
            <div className="dialog-content--icon-container">
              <Icon
                name="notice-warning"
              />
            </div>
            <div className="dialog-content--content-container">
              <span className="dialog-content--content-container--dialog-title dialog-content-title-font">{getText(locId, 'languageDialogQuestion1', 'LanguageSelect')}</span>
              <p className="dialog-content--content-container--dialog-title dialog-content-font">{getText(locId, 'languageDialogQuestion2', 'LanguageSelect')}</p>
              <span className="dialog-content-title-font">{getText(locId, 'languageDialogTitle1', 'LanguageSelect')}</span>
              <p className="dialog-content-font">{getText(locId, 'languageDialogTitle2', 'LanguageSelect')}</p>
            </div>
          </div>

        </Dialog>)
        }
      </div>
    </div>

  ) : (
    <></>
  );
};

LanguageSelect.propTypes = {
  addBeforeUnloadEventListener: PropTypes.func.isRequired,
  removeBeforeUnloadEventListener: PropTypes.func.isRequired,
}

export default LanguageSelect;
